import dragula from 'dragula'
import * as R from 'ramda'
import autoScroll from 'dom-autoscroller'

/**
 * This is the mixin for drag and drop functionality using dragula
 */
export default {
  data() {
    return {
      dragAndDropUnderProgress: {},
      drake: {}
    }
  },
  methods: {
    /**
     *
     * @param containers - array of refs on containers involved
     * @param onDrop - callback on drop of item
     * @param handleName - css className for icon that is being used for drag-drop
     * @param onDragEnd - callback on dragEnd
     * @param isFullyDraggable - true when entire region is draggable
     * @param scrollingContainers - array of arrays of scrolling containers within scrollable area.
     * @param context - name of the context. This is required when enableDragAndDrop is called in different contexts from a single component. e.g In GoalViewer, KRs and Child goals both are re-arrangeable within their containers.
     * It is array of arrays because for handling for horizontal and vertical scroll handling, we need       to call autoScroll separately
     */
    enableDragAndDrop({
      containers,
      onDrop,
      handleName = 'gripper-icon',
      onDragEnd,
      isFullyDraggable,
      preventDropInSameContainer,
      preventDropAtEnd,
      scrollingContainers = [[document.querySelector('#main-scrolling')]],
      context = 'root'
    }) {
      const self = this
      const contextInstance = self.drake[context]
      contextInstance?.destroy()
      this.drake[context] = dragula(containers, {
        moves: function(el, container, handle) {
          return handle.classList.contains(handleName) || isFullyDraggable
        },
        accepts(_, target, source, beforeEl) {
          return (
            (!preventDropInSameContainer || target !== source) && (!preventDropAtEnd || beforeEl)
          )
        }
      })
        .on('drag', el => this.onDragulaDrag(el, containers, context))
        .on('drop', function(el, container, targetContainer, beforeEl) {
          self.onDragulaDrop(el, container, targetContainer, beforeEl, onDrop, context)
        })
        .on('dragend', function() {
          containers.map(container => container.classList.remove('drag-active'))
          onDragEnd && onDragEnd()
        })
      scrollingContainers.forEach(containers => {
        autoScroll(containers, {
          margin: 20,
          maxSpeed: 5,
          scrollWhenOutside: true,
          autoScroll: function() {
            return this.down && self.drake[context]?.dragging
          }
        })
      })
    },
    onDragulaDrag(el, containers, context) {
      if (this.dragAndDropUnderProgress[context]) {
        // API request for one drag is already under progress. HALT
        this.drake[context].cancel()
        return
      }
      // doing selector over document because there can be an item selected outside the container as well
      const selected = document.querySelector('.select-list-item.selected')
      selected && selected.classList.remove('selected')
      el.classList.add('selected')
      containers.map(container => container.classList.add('drag-active'))
    },
    /**
     *
     * @param el
     * @param container - container in which element is dropped
     * @param targetContainer -  container that element belonged to
     * @param beforeEl
     * @param onDrop
     * @param context
     * @returns {Promise<void>}
     */
    async onDragulaDrop(el, container, targetContainer, beforeEl, onDrop, context) {
      let afterId, afterEl, beforeId
      if (beforeEl) {
        // This expects that key of the list's vnode will contain id of the entity
        beforeId = beforeEl.dataset.id
        // doing toString() check since dataset may have converted number to string
        const previousElement = beforeEl.previousElementSibling
        afterEl = previousElement && previousElement.previousElementSibling
        if (afterEl) {
          afterId = afterEl.dataset.id
        }
      } else {
        const secondLast = container.lastChild.previousElementSibling
        afterId = secondLast ? secondLast.dataset.id : undefined
      }
      const subjectId = el.dataset.id
      this.dragAndDropUnderProgress[context] = true
      await onDrop({ beforeId, afterId, subjectId, container, targetContainer })
      this.dragAndDropUnderProgress[context] = false
    },
    /**
     * This function changes position of entity with id - srcId. Entity with id - srcId is moved to the position before entity with id - beforeId
     * @param entities
     * @param srcId
     * @param beforeId
     * @returns {entities[]}
     */
    shiftItem(entities, srcId, beforeId) {
      const srcIndex = entities.findIndex(R.propEq('id', srcId))
      let beforeIndex = entities.findIndex(R.propEq('id', beforeId))
      if (R.isNil(beforeIndex)) {
        beforeIndex = -1
      } else if (srcIndex < beforeIndex) {
        beforeIndex--
      }
      return R.move(srcIndex, beforeIndex, entities)
    }
  }
}
